import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useTrip, TripData, FlightSegment, HotelReservation, CarReservation, CruiseReservation, Activity, ItemPricing } from "@/hooks/useTrips";
import { useProfile } from "@/hooks/useProfile";
import { useExchangeRates, convertCurrency, calculateCPP, formatCurrency } from "@/lib/currency";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { CurrencySelector } from "@/components/trips/CurrencySelector";
import { ArrowLeft, Plane, Hotel, Car, Ship, Calendar, Pencil, DollarSign, CreditCard, Coins, TrendingUp } from "lucide-react";
import { safeFormat } from "@/lib/formatDateTime";
import { EditFlightDialog } from "@/components/trips/dialogs/EditFlightDialog";
import { EditHotelDialog } from "@/components/trips/dialogs/EditHotelDialog";
import { EditCarDialog } from "@/components/trips/dialogs/EditCarDialog";
import { EditCruiseDialog } from "@/components/trips/dialogs/EditCruiseDialog";
import { EditActivityDialog } from "@/components/trips/dialogs/EditActivityDialog";

type ItemType = "flights" | "hotels" | "cars" | "cruises" | "activities";

// Flight group for PNR-based grouping
interface FlightGroup {
  confirmationNumber: string;
  flights: FlightSegment[];
  indices: number[];
  mergedPricing: ItemPricing;
}

// Category colors for badges
const categoryColors: Record<string, string> = {
  flights: "bg-blue-100 text-blue-700",
  hotels: "bg-purple-100 text-purple-700",
  cars: "bg-gray-100 text-gray-700",
  cruises: "bg-orange-100 text-orange-700",
  activities: "bg-green-100 text-green-700",
};

const categoryIcons: Record<string, React.ReactNode> = {
  flights: <Plane className="h-4 w-4" />,
  hotels: <Hotel className="h-4 w-4" />,
  cars: <Car className="h-4 w-4" />,
  cruises: <Ship className="h-4 w-4" />,
  activities: <Calendar className="h-4 w-4" />,
};

const TripCosts = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { data: trip, isLoading: tripLoading } = useTrip(tripId);
  const { data: profile } = useProfile();
  const preferredCurrency = profile?.preferred_currency || "USD";
  const { data: exchangeRates } = useExchangeRates("USD");

  const [editingItem, setEditingItem] = useState<{ type: ItemType; index: number } | null>(null);

  // Extract trip data
  const tripData = useMemo(() => (trip?.trip_data as TripData) || {}, [trip?.trip_data]);
  const flights = useMemo(() => tripData.flights || [], [tripData.flights]);
  const hotels = useMemo(() => tripData.hotels || [], [tripData.hotels]);
  const cars = useMemo(() => tripData.cars || [], [tripData.cars]);
  const cruises = useMemo(() => tripData.cruises || [], [tripData.cruises]);
  const activities = useMemo(() => tripData.activities || [], [tripData.activities]);

  // Currency conversion helper - must be before useMemo hooks that depend on it
  const convertAmount = useCallback((amount: number | undefined, currency: string | undefined) => {
    if (!amount) return 0;
    if (!currency || currency === preferredCurrency) return amount;
    if (!exchangeRates) return amount;
    return convertCurrency(amount, currency, preferredCurrency, exchangeRates);
  }, [preferredCurrency, exchangeRates]);

  // Group flights by confirmation number (PNR)
  const flightGroups = useMemo(() => {
    const groups = new Map<string, { flights: FlightSegment[]; indices: number[] }>();

    flights.forEach((flight, index) => {
      const key = flight.confirmation_number || `__solo_${index}_${flight.flight_number}`;
      const group = groups.get(key) || { flights: [], indices: [] };
      group.flights.push(flight);
      group.indices.push(index);
      groups.set(key, group);
    });

    const result: FlightGroup[] = [];

    groups.forEach((group, key) => {
      let retail_price: number | undefined;
      let retail_currency: string | undefined;
      let total_price_paid = 0;
      let total_taxes_fees = 0;
      let total_points_used = 0;
      let price_paid_currency: string | undefined;
      let points_program: string | undefined;

      for (const flight of group.flights) {
        if (flight.pricing) {
          if (retail_price === undefined && flight.pricing.retail_price) {
            retail_price = flight.pricing.retail_price;
            retail_currency = flight.pricing.retail_currency;
          }
          if (flight.pricing.price_paid) {
            total_price_paid += flight.pricing.price_paid;
            price_paid_currency = flight.pricing.price_paid_currency || price_paid_currency;
          }
          if (flight.pricing.taxes_fees_paid) {
            total_taxes_fees += flight.pricing.taxes_fees_paid;
          }
          if (flight.pricing.points_used) {
            total_points_used += flight.pricing.points_used;
            points_program = flight.pricing.points_program || points_program;
          }
        }
      }

      const mergedPricing: ItemPricing = {
        retail_price,
        retail_currency,
        price_paid: total_price_paid > 0 ? total_price_paid : undefined,
        price_paid_currency,
        taxes_fees_paid: total_taxes_fees > 0 ? total_taxes_fees : undefined,
        points_used: total_points_used > 0 ? total_points_used : undefined,
        points_program,
      };

      result.push({
        confirmationNumber: key.startsWith('__solo_') ? '' : key,
        flights: group.flights,
        indices: group.indices,
        mergedPricing,
      });
    });

    return result;
  }, [flights]);

  // Calculate category totals for sidebar - must be before early returns
  const categoryTotals = useMemo(() => {
    const totals: Record<string, number> = { flights: 0, hotels: 0, cars: 0, cruises: 0, activities: 0 };

    flightGroups.forEach((group) => {
      if (group.mergedPricing.price_paid) {
        totals.flights += convertAmount(group.mergedPricing.price_paid, group.mergedPricing.price_paid_currency);
      }
      if (group.mergedPricing.taxes_fees_paid) {
        totals.flights += convertAmount(group.mergedPricing.taxes_fees_paid, group.mergedPricing.price_paid_currency);
      }
    });
    hotels.forEach((h) => {
      if (h.pricing?.price_paid) totals.hotels += convertAmount(h.pricing.price_paid, h.pricing.price_paid_currency);
      if (h.pricing?.taxes_fees_paid) totals.hotels += convertAmount(h.pricing.taxes_fees_paid, h.pricing.price_paid_currency);
    });
    cars.forEach((c) => {
      if (c.pricing?.price_paid) totals.cars += convertAmount(c.pricing.price_paid, c.pricing.price_paid_currency);
      if (c.pricing?.taxes_fees_paid) totals.cars += convertAmount(c.pricing.taxes_fees_paid, c.pricing.price_paid_currency);
    });
    cruises.forEach((cr) => {
      if (cr.pricing?.price_paid) totals.cruises += convertAmount(cr.pricing.price_paid, cr.pricing.price_paid_currency);
      if (cr.pricing?.taxes_fees_paid) totals.cruises += convertAmount(cr.pricing.taxes_fees_paid, cr.pricing.price_paid_currency);
    });
    activities.forEach((a) => {
      if (a.pricing?.price_paid) totals.activities += convertAmount(a.pricing.price_paid, a.pricing.price_paid_currency);
      if (a.pricing?.taxes_fees_paid) totals.activities += convertAmount(a.pricing.taxes_fees_paid, a.pricing.price_paid_currency);
    });

    return totals;
  }, [flightGroups, hotels, cars, cruises, activities, convertAmount]);

  // Count total items - must be before early returns
  const totalItems = flightGroups.length + hotels.length + cars.length + cruises.length + activities.length;

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [user, authLoading, navigate]);

  if (authLoading || tripLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Trip Not Found</h1>
            <Link to="/trips">
              <Button>Back to Trips</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Calculate totals
  const calculateTotals = () => {
    let totalCashPaid = 0;
    let totalTaxesFees = 0;
    let totalRetail = 0;
    let totalPointsUsed = 0;

    const processItem = (pricing?: ItemPricing) => {
      if (!pricing) return;
      if (pricing.price_paid) {
        totalCashPaid += convertAmount(pricing.price_paid, pricing.price_paid_currency);
      }
      if (pricing.taxes_fees_paid) {
        totalTaxesFees += convertAmount(pricing.taxes_fees_paid, pricing.price_paid_currency);
      }
      if (pricing.retail_price) {
        totalRetail += convertAmount(pricing.retail_price, pricing.retail_currency);
      }
      if (pricing.points_used) {
        totalPointsUsed += pricing.points_used;
      }
    };

    flightGroups.forEach((group) => processItem(group.mergedPricing));
    hotels.forEach((h) => processItem(h.pricing));
    cars.forEach((c) => processItem(c.pricing));
    cruises.forEach((cr) => processItem(cr.pricing));
    activities.forEach((a) => processItem(a.pricing));

    const grandTotalCashPaid = totalCashPaid + totalTaxesFees;
    const overallCPP = totalPointsUsed > 0 && totalRetail > 0
      ? calculateCPP(totalPointsUsed, totalRetail, grandTotalCashPaid, 0)
      : null;

    return { totalCashPaid: grandTotalCashPaid, totalTaxesFees, totalBaseCashPaid: totalCashPaid, totalRetail, totalPointsUsed, overallCPP };
  };

  const totals = calculateTotals();

  const handleEditClose = () => setEditingItem(null);

  const getEditingFlight = () => editingItem?.type === "flights" ? flights[editingItem.index] : null;
  const getEditingHotel = () => editingItem?.type === "hotels" ? hotels[editingItem.index] : null;
  const getEditingCar = () => editingItem?.type === "cars" ? cars[editingItem.index] : null;
  const getEditingCruise = () => editingItem?.type === "cruises" ? cruises[editingItem.index] : null;
  const getEditingActivity = () => editingItem?.type === "activities" ? activities[editingItem.index] : null;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    // Handle ISO datetime by extracting date part
    const datePart = dateStr.split("T")[0];
    const result = safeFormat(datePart, "MMM d, yyyy");
    return result === "—" ? dateStr : result;
  };

  // Render a cost row
  const renderCostRow = (
    category: ItemType,
    name: string,
    description: string,
    date: string | undefined,
    pricing: ItemPricing | undefined,
    onEdit: () => void,
    key: string | number
  ) => {
    const retail = convertAmount(pricing?.retail_price, pricing?.retail_currency) || null;
    const baseCashPaid = convertAmount(pricing?.price_paid, pricing?.price_paid_currency) || null;
    const taxesFees = convertAmount(pricing?.taxes_fees_paid, pricing?.price_paid_currency) || null;
    const totalCashPaid = baseCashPaid !== null ? baseCashPaid + (taxesFees || 0) : null;
    const pointsUsed = pricing?.points_used;
    const cpp = pointsUsed && retail && totalCashPaid !== null
      ? calculateCPP(pointsUsed, retail, totalCashPaid, 0)
      : null;

    return (
      <tr key={key} className="hover:bg-muted/30 transition-colors">
        <td className="px-4 py-3">
          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${categoryColors[category]}`}>
            {categoryIcons[category]}
            {category.charAt(0).toUpperCase() + category.slice(1, -1)}
          </span>
        </td>
        <td className="px-4 py-3">
          <div className="font-medium">{name}</div>
          <div className="text-sm text-muted-foreground">{description}</div>
          {date && <div className="text-xs text-muted-foreground mt-0.5">{formatDate(date)}</div>}
        </td>
        <td className="px-4 py-3 text-right">
          {retail !== null && retail > 0 ? formatCurrency(retail, preferredCurrency) : "—"}
        </td>
        <td className="px-4 py-3 text-right">
          <div>
            {totalCashPaid !== null && totalCashPaid > 0 ? formatCurrency(totalCashPaid, preferredCurrency) : "—"}
          </div>
          {baseCashPaid !== null && taxesFees !== null && taxesFees > 0 && (
            <div className="text-xs text-muted-foreground">
              incl. {formatCurrency(taxesFees, preferredCurrency)} fees
            </div>
          )}
        </td>
        <td className="px-4 py-3 text-right">
          {pointsUsed ? (
            <div>
              <div>{pointsUsed.toLocaleString()}</div>
              {pricing?.points_program && (
                <div className="text-xs text-muted-foreground">{pricing.points_program}</div>
              )}
            </div>
          ) : "—"}
        </td>
        <td className="px-4 py-3 text-right">
          {cpp !== null ? `${cpp.toFixed(2)}¢` : "—"}
        </td>
        <td className="px-4 py-3">
          <button
            onClick={onEdit}
            className="p-1.5 hover:bg-muted rounded transition-colors"
          >
            <Pencil className="h-4 w-4 text-muted-foreground" />
          </button>
        </td>
      </tr>
    );
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />

      {/* Hero Header with Gradient */}
      <div className="gradient-hero text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div>
              <Link to={`/trips/${tripId}`} className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 text-sm transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Back to trip
              </Link>
              <h1 className="text-3xl font-bold mb-2">{trip.display_name}</h1>
              <p className="text-white/80">Cost Management</p>
            </div>
            <div className="text-left lg:text-right">
              <div className="text-sm text-white/70 mb-1">Total Trip Cost</div>
              <div className="text-3xl font-bold">{formatCurrency(totals.totalCashPaid, preferredCurrency)}</div>
              {totals.totalPointsUsed > 0 && (
                <div className="text-sm text-white/90 mt-1">
                  {totals.totalPointsUsed.toLocaleString()} points used
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-card border-b border-border sticky top-16 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex gap-2 sm:gap-6 overflow-x-auto whitespace-nowrap">
            <Link
              to={`/trips/${tripId}`}
              className="px-4 py-3 text-muted-foreground hover:text-foreground border-b-2 border-transparent transition-colors"
            >
              Timeline
            </Link>
            <div className="px-4 py-3 text-primary border-b-2 border-primary font-medium">
              Costs
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 py-8 w-full">
        {/* 2-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm">Total Cost</span>
                </div>
                <div className="text-2xl font-bold">{formatCurrency(totals.totalCashPaid, preferredCurrency)}</div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm">Retail Value</span>
                </div>
                <div className="text-2xl font-bold">{formatCurrency(totals.totalRetail, preferredCurrency)}</div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <Coins className="w-4 h-4" />
                  <span className="text-sm">Points Used</span>
                </div>
                <div className="text-2xl font-bold">
                  {totals.totalPointsUsed > 0 ? totals.totalPointsUsed.toLocaleString() : "—"}
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <CreditCard className="w-4 h-4" />
                  <span className="text-sm">Items</span>
                </div>
                <div className="text-2xl font-bold">{totalItems}</div>
              </div>
            </div>

            {/* Savings Banner */}
            {totals.totalRetail > 0 && totals.totalCashPaid > 0 && totals.totalRetail > totals.totalCashPaid && (
              <div className="bg-gradient-to-r from-green-50 to-green-50/50 border border-green-200 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <div className="text-sm text-green-700 font-medium">Total Savings</div>
                    <div className="text-xs text-green-600">
                      {((1 - totals.totalCashPaid / totals.totalRetail) * 100).toFixed(0)}% off retail value
                    </div>
                  </div>
                  <div className="text-2xl font-bold text-green-700">
                    {formatCurrency(totals.totalRetail - totals.totalCashPaid, preferredCurrency)}
                  </div>
                </div>
              </div>
            )}

            {/* Currency Selector */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <h2 className="text-lg font-semibold">All Expenses</h2>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Currency:</span>
                <CurrencySelector className="w-28" />
              </div>
            </div>

            {/* Cost Items Table */}
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full min-w-[760px]">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium w-24">Category</th>
                    <th className="px-4 py-3 text-left text-sm font-medium">Description</th>
                    <th className="px-4 py-3 text-right text-sm font-medium w-28">Retail</th>
                    <th className="px-4 py-3 text-right text-sm font-medium w-32">Cash Paid</th>
                    <th className="px-4 py-3 text-right text-sm font-medium w-24">Points</th>
                    <th className="px-4 py-3 text-right text-sm font-medium w-16">CPP</th>
                    <th className="px-4 py-3 text-right text-sm font-medium w-12"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {totalItems === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">
                        No expenses added yet. Add items to your trip to track costs.
                      </td>
                    </tr>
                  ) : (
                    <>
                      {/* Flights */}
                      {flightGroups.map((group, idx) => {
                        const segments = group.flights.map(f => `${f.airline} ${f.flight_number}`).join(' + ');
                        const firstFlight = group.flights[0];
                        const lastFlight = group.flights[group.flights.length - 1];
                        const route = group.flights.length === 1
                          ? `${firstFlight.departure_airport} → ${firstFlight.arrival_airport}`
                          : `${firstFlight.departure_airport} → ${lastFlight.arrival_airport}`;
                        const date = firstFlight.departure_date || firstFlight.departure_time;

                        return renderCostRow(
                          "flights",
                          segments,
                          `${route}${group.confirmationNumber ? ` • PNR: ${group.confirmationNumber}` : ''}`,
                          date,
                          group.mergedPricing,
                          () => setEditingItem({ type: "flights", index: group.indices[0] }),
                          `flight-${idx}`
                        );
                      })}

                      {/* Hotels */}
                      {hotels.map((hotel, idx) =>
                        renderCostRow(
                          "hotels",
                          hotel.hotel_name,
                          hotel.city || hotel.address || "",
                          hotel.check_in_date,
                          hotel.pricing,
                          () => setEditingItem({ type: "hotels", index: idx }),
                          `hotel-${idx}`
                        )
                      )}

                      {/* Cars */}
                      {cars.map((car, idx) =>
                        renderCostRow(
                          "cars",
                          car.rental_company,
                          car.car_type || "",
                          car.pickup_date,
                          car.pricing,
                          () => setEditingItem({ type: "cars", index: idx }),
                          `car-${idx}`
                        )
                      )}

                      {/* Cruises */}
                      {cruises.map((cruise, idx) =>
                        renderCostRow(
                          "cruises",
                          cruise.ship_name,
                          cruise.cruise_line,
                          cruise.departure_date,
                          cruise.pricing,
                          () => setEditingItem({ type: "cruises", index: idx }),
                          `cruise-${idx}`
                        )
                      )}

                      {/* Activities */}
                      {activities.map((activity, idx) =>
                        renderCostRow(
                          "activities",
                          activity.name,
                          activity.location || "",
                          activity.date,
                          activity.pricing,
                          () => setEditingItem({ type: "activities", index: idx }),
                          `activity-${idx}`
                        )
                      )}
                    </>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Category Breakdown */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="font-semibold mb-4">By Category</h3>
              <div className="space-y-3">
                {Object.entries(categoryTotals).map(([category, total]) => {
                  if (total === 0) return null;
                  return (
                    <div key={category} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          category === 'flights' ? 'bg-blue-500' :
                          category === 'hotels' ? 'bg-purple-500' :
                          category === 'cars' ? 'bg-gray-500' :
                          category === 'cruises' ? 'bg-orange-500' :
                          'bg-green-500'
                        }`}></div>
                        <span className="text-sm capitalize">{category}</span>
                      </div>
                      <div className="font-medium">{formatCurrency(total, preferredCurrency)}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Points Summary */}
            {totals.totalPointsUsed > 0 && (
              <div className="bg-gradient-to-br from-accent/10 to-accent/5 border border-accent/20 rounded-lg p-6">
                <h3 className="font-semibold mb-4">Points Summary</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Total Points Used</span>
                    <span className="font-semibold">{totals.totalPointsUsed.toLocaleString()}</span>
                  </div>
                  {totals.overallCPP !== null && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Average CPP</span>
                        <span className="font-semibold">{totals.overallCPP.toFixed(2)}¢</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Points Value</span>
                        <span className="font-semibold text-accent">
                          {formatCurrency((totals.totalPointsUsed * totals.overallCPP) / 100, preferredCurrency)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Savings Summary */}
            {totals.totalRetail > totals.totalCashPaid && totals.totalRetail > 0 && (
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-semibold mb-4">Savings Breakdown</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Retail Value</span>
                    <span className="font-medium">{formatCurrency(totals.totalRetail, preferredCurrency)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">You Paid</span>
                    <span className="font-medium">{formatCurrency(totals.totalCashPaid, preferredCurrency)}</span>
                  </div>
                  {totals.totalTaxesFees > 0 && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">Includes taxes/fees</span>
                      <span className="text-muted-foreground">{formatCurrency(totals.totalTaxesFees, preferredCurrency)}</span>
                    </div>
                  )}
                  <div className="border-t border-border pt-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-green-600">Total Saved</span>
                      <span className="font-bold text-green-600">
                        {formatCurrency(totals.totalRetail - totals.totalCashPaid, preferredCurrency)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />

      {/* Edit Dialogs */}
      {getEditingFlight() && (
        <EditFlightDialog
          flight={getEditingFlight()!}
          tripId={trip.id}
          index={editingItem!.index}
          open={true}
          onOpenChange={(open) => !open && handleEditClose()}
        />
      )}
      {getEditingHotel() && (
        <EditHotelDialog
          hotel={getEditingHotel()!}
          tripId={trip.id}
          index={editingItem!.index}
          open={true}
          onOpenChange={(open) => !open && handleEditClose()}
        />
      )}
      {getEditingCar() && (
        <EditCarDialog
          car={getEditingCar()!}
          tripId={trip.id}
          index={editingItem!.index}
          open={true}
          onOpenChange={(open) => !open && handleEditClose()}
        />
      )}
      {getEditingCruise() && (
        <EditCruiseDialog
          cruise={getEditingCruise()!}
          tripId={trip.id}
          index={editingItem!.index}
          open={true}
          onOpenChange={(open) => !open && handleEditClose()}
        />
      )}
      {getEditingActivity() && (
        <EditActivityDialog
          activity={getEditingActivity()!}
          tripId={trip.id}
          index={editingItem!.index}
          open={true}
          onOpenChange={(open) => !open && handleEditClose()}
        />
      )}
    </div>
  );
};

export default TripCosts;
